import { useLocation, useNavigate } from '@tanstack/react-router';
import { LogOut } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Brand from '@/components/common/Brand';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import { useAuth } from '@/hooks/use-auth';
import { updateRouterContext } from '@/lib/router/router-utils';

import { NAV_ITEMS, SIDEBAR_WIDTH } from '../../constants/dashboard.constants';
import { MobileDrawerNavSection } from './MobileDrawerNavSection';

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileDrawer({ isOpen, onClose }: MobileDrawerProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    updateRouterContext();
    navigate({ to: '/login' });
  };

  // Filter items into sections (matching desktop sidebar)
  const dashboardItems = NAV_ITEMS.filter((item) => item.key === 'dashboard');
  const applicationItems = NAV_ITEMS.filter(
    (item) =>
      item.key === 'pipeline' ||
      item.key === 'forms' ||
      item.key === 'clients' ||
      item.key === 'reports' ||
      item.key === 'notifications'
  );
  const settingsItems = NAV_ITEMS.filter((item) => item.key === 'settings');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      <aside
        className="absolute left-0 top-0 z-10 flex h-full flex-col justify-between bg-surface-inverse p-5 py-8"
        style={{ width: SIDEBAR_WIDTH }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-8">
          <div className="px-2">
            <Brand link="/dashboard" variant="light" />
          </div>

          <nav className="flex flex-col gap-6">
            <MobileDrawerNavSection
              label={t('nav.sections.dashboard')}
              items={dashboardItems}
            />
            <MobileDrawerNavSection
              label={t('nav.sections.applications')}
              items={applicationItems}
            />
            <MobileDrawerNavSection
              label={t('nav.sections.settings')}
              items={settingsItems}
            />
          </nav>
        </div>

        <div className="flex flex-col gap-4">
          <LanguageSwitcher variant="sidebar" />
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full cursor-pointer items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-foreground-inverse transition-colors hover:bg-white/10"
          >
            <LogOut className="h-5 w-5" />
            <span>{t('nav.logout')}</span>
          </button>
        </div>
      </aside>
    </div>
  );
}
